import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SystemCorrectionFormObservedField,
  SystemCorrectionDetail,
  SystemCorrectionFormPatch,
  SystemCorrectionProjectSearchResponse,
  SystemCorrectionProjectSearchResult,
  SystemCorrectionSectionSummary,
  SystemCorrectionStoredUserData,
  SystemCorrectionTaskTreeItem,
} from '@/app/interfaces/system-correction';
import {
  emptySystemCorrectionProjectCodes,
  getSystemCorrectionSubmitRole,
  mapSystemCorrectionFormToPayload,
  mapSystemCorrectionDetailToForm,
  mapSystemCorrectionFieldsToSummary,
  normalizeSystemCorrectionFields,
  toSystemCorrectionStoredUserData,
} from '@/app/services/ia-services/system-correction.mapper';
import {
  ReviewComment,
  ReviewCommentsBlockComponent,
} from '@/app/shared/review-comments-block/review-comments-block.component';
import { SearchService } from '@/app/services/ia-services/search.service';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DatePickerComponent } from '@/app/shared/date-picker/date-picker.component';
import { taskFunctions } from '@/app/shared/mappers/task-function-label.mapper';
import { ObservedFieldAlertComponent } from '@/app/shared/observed-field-alert/observed-field-alert/observed-field-alert.component';
import { PillComponent } from '@/app/shared/pill/pill.component';
import { getError } from '@/app/utils/form-errors';
import { requiredNonEmptyArray } from '@/app/utils/required-non-empty-array.validator';
import { requiredProject } from '@/app/utils/required-project.validator';
import { requiredSelect } from '@/app/utils/required-select.validator';
import { DesyAngularModule } from 'desy-angular';

@Component({
  selector: 'app-system-correction',
  standalone: true,
  imports: [
    CommonModule,
    DesyAngularModule,
    ButtonsComponent,
    FormsModule,
    ReactiveFormsModule,
    PillComponent,
    DatePickerComponent,
    ReviewCommentsBlockComponent,
    ObservedFieldAlertComponent,
  ],
  templateUrl: './system-correction.component.html',
  styleUrl: './system-correction.component.scss',
})
export class SystemCorrectionComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly systemsService = inject(SystemsService);
  private readonly user = this.getStoredUserData();
  private readonly otherSystemTypeField = new FormControl({ value: '', disabled: true });
  private readonly otherDataTypeField = new FormControl({ value: '', disabled: true });

  protected readonly loading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly correctionFields = signal<string[]>([]);
  protected readonly knownCorrectionFields = signal<SystemCorrectionFormObservedField[]>([]);
  protected readonly unknownCorrectionFields = signal<string[]>([]);
  protected readonly observedFields = signal<SystemCorrectionFormObservedField[]>([]);
  protected readonly pendingObservedFields = signal<SystemCorrectionFormObservedField[]>([]);
  protected readonly correctionFieldsSummary = signal<SystemCorrectionSectionSummary[]>([]);
  protected readonly initialObservedFieldsCount = signal(0);
  protected readonly formSubmitted = signal(false);
  protected readonly projectSearchLoading = signal(false);
  protected readonly minSelectableDate = this.getTodayDate();
  protected readonly otherFunctions = signal<string[]>([]);
  protected readonly otherFunction = signal('');
  protected readonly isProjectAutofilled = signal(false);
  protected readonly projectList = signal<SystemCorrectionProjectSearchResult[]>([]);
  protected readonly autogeneratedItems: SystemCorrectionTaskTreeItem[];
  protected reviewComments: ReviewComment[] = [];

  protected readonly name = 'taskOrFunctions';
  protected readonly id = '1';
  protected readonly type: 'radio' | 'checkbox' = 'checkbox';
  protected readonly idPrefix = 'item';
  protected readonly getError = getError;
  protected readonly canSubmitCorrection = computed(() => this.pendingObservedFields().length === 0);

  private projectSearchRequestId = 0;
  private initialObservedFields: SystemCorrectionFormObservedField[] = [];

  protected readonly typeSystem = [
    { label: 'Sistema conversacional', value: 8 },
    { label: 'Sistema de análisis y extracción de información', value: 7 },
    { label: 'Sistema de recomendación y personalización', value: 6 },
    { label: 'Sistema predictivo y de apoyo de la decisión', value: 5 },
    { label: 'Sistema para la automatización inteligente de procesos', value: 4 },
    { label: 'Sistema de detección y supervisión', value: 3 },
    { label: 'Sistema a medida con funciones críticas o sensibles', value: 2 },
    { label: 'Otros', value: 1 },
  ];
  protected readonly tasksFunctions = taskFunctions;
  protected readonly explainabilityLevelOptions = [
    { label: 'Bajo', value: 1 },
    { label: 'Medio', value: 2 },
    { label: 'Alto', value: 3 },
  ];
  protected readonly administrationRoleOptions = [
    { value: 1, label: 'Representante' },
    { value: 2, label: 'Fabricante' },
    { value: 3, label: 'Importador' },
    { value: 4, label: 'Distribuidor' },
    { value: 5, label: 'Desplegador' },
    { value: 6, label: 'Proveedor' },
  ];
  protected readonly dataTypeOptions = [
    { value: 1, label: 'Documentos' },
    { value: 2, label: 'Páginas' },
    { value: 3, label: 'Consultas' },
    { value: 4, label: 'Registros' },
    { value: 5, label: 'Imágenes' },
    { value: 6, label: 'Vídeo/Audio' },
    { value: 7, label: 'Datos estructurados' },
    { value: 8, label: 'Formularios' },
    { value: 9, label: 'Otros' },
  ];
  protected readonly systemProviderOptions = [
    { value: 'Interno', label: 'Interno' },
    { value: 'Externo', label: 'Externo' },
    { value: 'Ambos', label: 'Ambos' },
  ];
  protected readonly systemClassificationOptions = [
    { value: 'inaceptable', label: 'Inaceptable' },
    { value: 'alto', label: 'Alto' },
    { value: 'limitado', label: 'Limitado' },
    { value: 'minimo', label: 'Mínimo' },
  ];

  protected readonly form: FormGroup = this.fb.group({
    projectCodes: [emptySystemCorrectionProjectCodes(), requiredProject],
    systemName: ['', [Validators.required, Validators.pattern(/^[^:#]*$/)]],
    systemType: [null, Validators.required],
    otherSystemType: this.otherSystemTypeField,
    functionOfSystem: [[], requiredNonEmptyArray],
    otherFunctions: [[], []],
    description: ['', [Validators.required, Validators.maxLength(500)]],
    estimatedDeployDate: [null, Validators.required],
    estimatedFunctionalInputVolume: [null, Validators.required],
    dataType: [0, requiredSelect],
    otherDataType: this.otherDataTypeField,
    administrationRole: [0, requiredSelect],
    explainabilityLevel: [0, requiredSelect],
    contractingImport: [null, Validators.required],
    annualAmount: [null, Validators.required],
    estimatedAnnualSavings: [null, Validators.required],
    lastModifiedDate: [null],
    contractValidity: [null, [Validators.min(0), Validators.required]],
    contractValidityDate: [null, Validators.required],
    aiSystemProviderType: [null, Validators.required],
    aiProviderName: [null, [Validators.required, Validators.maxLength(100)]],
    aiModelName: [null, [Validators.required, Validators.maxLength(200)]],
    systemClassification: [null, Validators.required],
  });

  constructor() {
    this.autogeneratedItems = this.tasksFunctions.map((group) => ({
      id: group.value,
      value: group.value,
      label: group.label,
      expanded: false,
      disabled: false,
      sub: {
        items: group.children.map((child) => ({
          id: child.value,
          value: child.value,
          label: child.label,
          expanded: false,
          disabled: false,
          sub: { items: [] },
        })),
      },
    }));

    this.form.get('systemType')?.valueChanges.subscribe((value) => {
      this.onSystemTypeChange(value);
    });
    this.form.get('dataType')?.valueChanges.subscribe((value) => {
      this.onDataTypeChange(value);
    });
    this.form.get('functionOfSystem')?.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
    this.form.get('otherFunctions')?.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  private getTodayDate(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  async ngOnInit(): Promise<void> {
    const idSystem = this.route.snapshot.paramMap.get('id');

    if (!idSystem) {
      this.errorMessage.set('No se ha encontrado el identificador del sistema.');
      this.loading.set(false);
      return;
    }

    try {
      const detail = await this.systemsService.getSystemCorrectionDetail(idSystem);
      this.processCorrectionFields(detail.correctionFields ?? []);
      this.reviewComments = this.buildReviewComments(detail);
      this.patchFormWithDetail(detail);
      this.applyObservedValidations();
      this.updateObservedFieldsFromFormStatus();
    } catch {
      this.errorMessage.set('No se ha podido cargar el detalle de subsanación del sistema.');
    } finally {
      this.loading.set(false);
    }
  }

  protected get otherSystemTypeControl(): FormControl {
    return this.otherSystemTypeField;
  }

  protected get otherDataTypeControl(): FormControl {
    return this.otherDataTypeField;
  }

  protected get isOtherDataTypeSelected(): boolean {
    return Number(this.form.get('dataType')?.value) === 9;
  }

  protected backPage(): void {
    this.location.back();
  }

  protected disabledSubmit(): void {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();
  }

  protected async onSave(): Promise<void> {
    this.formSubmitted.set(true);
    this.form.markAllAsTouched();

    if (this.form.invalid || !this.canSubmitCorrection()) {
      return;
    }

    const idSystem = this.route.snapshot.paramMap.get('id');
    const role = getSystemCorrectionSubmitRole(this.user);

    if (!idSystem || !role) {
      return;
    }

    const payload = mapSystemCorrectionFormToPayload(
      this.form.getRawValue() as SystemCorrectionFormPatch,
      idSystem,
      role
    );

    try {
      await this.systemsService.submitSystemCorrection(payload);
      await this.router.navigate(['/catalogo-ia/sistemas-de-ia/listado-sistemas']);
    } catch {
      return;
    }
  }

  protected isObserved(field: SystemCorrectionFormObservedField): boolean {
    return this.pendingObservedFields().includes(field);
  }

  protected onSystemTypeChange(value: number | null): void {
    if (value === 1) {
      this.otherSystemTypeControl.enable();
      this.otherSystemTypeControl.setValidators([Validators.required, Validators.pattern(/^[^:#]*$/)]);
    } else {
      this.otherSystemTypeControl.clearValidators();
      this.otherSystemTypeControl.setValue(null);
      this.otherSystemTypeControl.disable();
    }

    this.otherSystemTypeControl.updateValueAndValidity();
  }

  protected onDataTypeChange(value: number | null): void {
    if (Number(value) === 9) {
      this.otherDataTypeControl.enable();
      this.otherDataTypeControl.setValidators([Validators.required, Validators.pattern(/^[^:#]*$/)]);
    } else {
      this.otherDataTypeControl.clearValidators();
      this.otherDataTypeControl.setValue(null);
      this.otherDataTypeControl.disable();
    }

    this.otherDataTypeControl.updateValueAndValidity();
    this.updateOtherDataTypeObservedState();
  }

  private updateOtherDataTypeObservedState(): void {
    const field: SystemCorrectionFormObservedField = 'otherDataType';

    if (!this.initialObservedFields.includes(field)) {
      return;
    }

    const isPending = this.pendingObservedFields().includes(field);
    const shouldBePending = this.otherDataTypeControl.enabled && this.otherDataTypeControl.invalid;

    if (shouldBePending && !isPending) {
      this.pendingObservedFields.update((fields) => [...fields, field]);
    }

    if (!shouldBePending && isPending) {
      this.pendingObservedFields.update((fields) => fields.filter((pendingField) => pendingField !== field));
    }
  }

  protected async onSearchProjects(event: { query?: string }): Promise<void> {
    const query = `${event.query ?? ''}`.trim();

    if (query.length < 3) {
      this.projectList.set([]);
      this.projectSearchLoading.set(false);
      return;
    }

    const requestId = ++this.projectSearchRequestId;
    this.projectList.set([]);
    this.projectSearchLoading.set(true);

    try {
      const response = await this.searchService.search<SystemCorrectionProjectSearchResponse>({
        search: query,
        busqueda: 'proyectos',
      });

      if (requestId === this.projectSearchRequestId) {
        this.projectList.set(response.data ?? []);
      }
    } finally {
      if (requestId === this.projectSearchRequestId) {
        this.projectSearchLoading.set(false);
      }
    }
  }

  protected onProjectSelected(event: { value: SystemCorrectionProjectSearchResult }): void {
    const selected = event.value;
    this.projectList.set([]);
    this.form.patchValue({
      projectCodes: {
        codigo_proyecto: selected.codigo_proyecto,
        codigo_proyecto_solicitud: selected.codigo_proyecto_solicitud,
        proyecto: selected.proyecto,
        codigo_organismo: selected.codigo_organismo,
      },
    });
  }

  protected addFunctions(event?: MouseEvent): void {
    event?.preventDefault();
    const value = this.otherFunction().trim();

    if (!value) {
      return;
    }

    this.syncOtherFunctions([...this.otherFunctions(), value], true);
    this.otherFunction.set('');
  }

  protected removeFunctions(item: string): void {
    this.syncOtherFunctions(
      this.otherFunctions().filter((fn) => fn !== item),
      true
    );
  }

  private buildReviewComments(detail: SystemCorrectionDetail): ReviewComment[] {
    return [
      { userRole: 'GA', comment: detail.commentSupportGroup ?? null, state: detail.supportGroupState ?? null },
      {
        userRole: 'RF',
        comment: detail.commentRevisorFuncional ?? null,
        state: detail.functionalReviewerState ?? null,
      },
      { userRole: 'RJ', comment: detail.commentRevisorJuridico ?? null, state: detail.legalReviewerState ?? null },
      { userRole: 'RT', comment: detail.commentRevisorTecnico ?? null, state: detail.technicalReviewerState ?? null },
      { userRole: 'RDPD', comment: detail.commentRevisorDpd ?? null, state: detail.dpdReviewerState ?? null },
      { userRole: 'JS', comment: detail.commentServiceManager ?? null, state: detail.serviceManagerState ?? null },
    ];
  }

  private patchFormWithDetail(detail: SystemCorrectionDetail): void {
    const formValue = mapSystemCorrectionDetailToForm(detail, this.user);
    this.form.patchValue(formValue);
    this.syncOtherFunctions(formValue.otherFunctions);
    this.isProjectAutofilled.set(!!formValue.projectCodes.proyecto);
  }

  private syncOtherFunctions(functions: string[], markAsDirty = false): void {
    const normalizedFunctions = functions.map((fn) => fn.trim()).filter((fn) => fn !== '');
    const control = this.form.get('otherFunctions');

    this.otherFunctions.set(normalizedFunctions);
    control?.setValue(normalizedFunctions);

    if (markAsDirty) {
      control?.markAsDirty();
    }

    control?.updateValueAndValidity();
    this.updateFunctionOfSystemValidators();
  }

  private updateFunctionOfSystemValidators(): void {
    const control = this.form.get('functionOfSystem');

    if (this.otherFunctions().length > 0) {
      control?.clearValidators();
    } else {
      control?.setValidators(requiredNonEmptyArray);
    }

    control?.updateValueAndValidity({ emitEvent: false });
  }

  private processCorrectionFields(correctionFields: string[]): void {
    const observedFields = normalizeSystemCorrectionFields(correctionFields);
    const knownCorrectionFields = observedFields.knownCorrectionFields;

    this.correctionFields.set(observedFields.correctionFields);
    this.knownCorrectionFields.set(knownCorrectionFields);
    this.unknownCorrectionFields.set(observedFields.unknownCorrectionFields);
    this.initialObservedFields = knownCorrectionFields;
    this.initialObservedFieldsCount.set(knownCorrectionFields.length);
    this.correctionFieldsSummary.set(mapSystemCorrectionFieldsToSummary(knownCorrectionFields));
    this.observedFields.set(knownCorrectionFields);
    this.pendingObservedFields.set(knownCorrectionFields);
  }

  private applyObservedValidations(): void {
    this.observedFields().forEach((field) => {
      const control = this.form.get(field);

      if (!control) {
        return;
      }

      control.addValidators(Validators.required);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private updateObservedFieldsFromFormStatus(): void {
    this.initialObservedFields.forEach((field) => {
      const control = this.form.get(field);

      if (!control) {
        return;
      }

      control.valueChanges.subscribe(() => {
        const isPending = this.pendingObservedFields().includes(field);

        if (control.valid && isPending) {
          this.pendingObservedFields.update((fields) => fields.filter((pendingField) => pendingField !== field));
        }

        if (control.invalid && !isPending) {
          this.pendingObservedFields.update((fields) => [...fields, field]);
        }
      });
    });
  }

  private getStoredUserData(): SystemCorrectionStoredUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return toSystemCorrectionStoredUserData(JSON.parse(storedUserData));
  }
}
